import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const EMOJI_OPTIONS = [
  '🤝',
  '❤️',
  '💛',
  '💚',
  '💙',
  '💜',
  '🧡',
  '🖤',
  '✨',
  '🌟',
  '⭐',
  '🔥',
  '🌱',
  '🌿',
  '🌳',
  '🌍',
  '🌈',
  '☀️',
  '🌙',
  '💡',
  '🧠',
  '👁️',
  '🗣️',
  '👂',
  '🙌',
  '👏',
  '💪',
  '🫶',
  '🤗',
  '😊',
  '😄',
  '😌',
  '😇',
  '🤩',
  '😎',
  '🧐',
  '🫡',
  '✌️',
  '👍',
  '🙏',
  '🎯',
  '🏆',
  '🥇',
  '🏅',
  '🎖️',
  '🛡️',
  '⚖️',
  '🔑',
  '🧭',
  '📚',
  '✏️',
  '📝',
  '🏁',
  '🚀',
  '🕊️',
  '🐾',
  '🦋',
  '🌸',
  '🏠',
  '🏫',
  '👨‍👩‍👧',
  '👩‍🏫',
  '🧑‍🤝‍🧑',
  '👶',
  '🧒',
  '🎉',
  '🎈',
  '🎁',
  '🔔',
  '⏰',
  '📌',
  '✅',
  '❗',
  '💬',
  '📣',
  '🧩',
  '🎨',
  '🎵',
  '🎸',
  '⚽',
  '🏀',
  '🚴',
  '🧘'
] as const;

interface EmojiPickerFieldProps {
  value: string;
  onChange: (emoji: string) => void;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

type PaletteCoords = {
  top: number;
  left: number;
  width: number;
};

export function EmojiPickerField({
  value,
  onChange,
  required = false,
  placeholder = 'Select emoji',
  disabled = false,
  id
}: EmojiPickerFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const listboxId = `${fieldId}-palette`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<PaletteCoords | null>(null);

  function updateCoords() {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, 260);
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    const preferredTop = rect.bottom + 6;
    const approxHeight = 230;
    const top =
      preferredTop + approxHeight > window.innerHeight - 8
        ? Math.max(8, rect.top - approxHeight - 6)
        : preferredTop;
    setCoords({
      top,
      left: Math.max(8, left),
      width
    });
  }

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || paletteRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    function handleReposition() {
      updateCoords();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open]);

  function toggleOpen() {
    if (disabled) return;
    setOpen((current) => !current);
  }

  function selectEmoji(emoji: string) {
    onChange(emoji);
    setOpen(false);
  }

  const palette =
    open && coords
      ? createPortal(
          <div
            ref={paletteRef}
            id={listboxId}
            className="emoji-picker-palette"
            role="listbox"
            aria-label="Emoji palette"
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width
            }}
          >
            {EMOJI_OPTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                role="option"
                aria-selected={value === emoji}
                className={`emoji-picker-option${value === emoji ? ' is-selected' : ''}`}
                onClick={() => selectEmoji(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`emoji-picker-field${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        id={fieldId}
        className="emoji-picker-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        disabled={disabled}
        onClick={toggleOpen}
      >
        <span className={`emoji-picker-value${value ? '' : ' is-placeholder'}`}>
          {value || placeholder}
        </span>
        <span className="emoji-picker-chevron" aria-hidden>
          ▾
        </span>
      </button>

      <input
        type="text"
        value={value}
        required={required}
        tabIndex={-1}
        aria-hidden
        readOnly
        className="emoji-picker-required-input"
        onChange={() => undefined}
      />

      {palette}
    </div>
  );
}
